
import { Button } from "./ui/button";
import { Copy } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

interface CroppedIdProps {
  id: string;
  label?: string;
  size?: "sm" | "md" | "lg";
  showCopyButton?: boolean;
}

const CroppedId = ({
  id,
  label = "ID",
  size = "md",
  showCopyButton = false
}: CroppedIdProps) => {
  const { t, language } = useLanguage();

  // Crop ID based on size
  const getCroppedId = () => {
    if (!id) return "N/A";
    switch (size) {
      case "sm":
        return id.substring(0, 6);
      case "lg":
        return id.substring(0, 12);
      case "md":
      default:
        return id.substring(0, 8);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(id)
      .then(() => toast.success(`${label} ${t("common.copied")}`))
      .catch(() => toast.error(t("common.failed_copy")));
  };

  return (
    <div className="flex items-center">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger 
            data-full-id={id} // Keep the data attribute with full ID for download functionality
            className="text-m font-regular"
          >
            {getCroppedId()}
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">
            <p>{id}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {showCopyButton && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5 ml-1 text-gray-500 hover:text-gray-700" 
                onClick={copyToClipboard}
              >
                <Copy className="h-3 w-3" />
                <span className="sr-only">Copy {label.toLowerCase()}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>{language === 'de' ? `${label} kopieren` : `Copy ${label}`}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export default CroppedId;
